import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtTimeRange } from "@/lib/format";
import { ChevronLeft, ChevronRight, Building2, AlertTriangle, Layers } from "lucide-react";

/**
 * Cross-facility calendar. One week grid, one column per day, every booking
 * across every facility drawn as a chip in that day's column, coloured by
 * SHOW so at-a-glance you can see who owns what slot. When two productions
 * hold overlapping bookings in the same facility, we ring them amber and
 * label the count so double-books surface without you having to hunt.
 *
 * The backend `/api/facilities/schedule` already handles cross-tenant
 * masking: rehearsals from productions the viewer isn't in come back with
 * a "Booked (another production)" title. We colour those a neutral zinc.
 */

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfWeek(d) {
  const x = new Date(d);
  x.setDate(x.getDate() - x.getDay());
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

const PALETTE = [
  "#F59E0B", "#3B82F6", "#EF4444", "#10B981", "#8B5CF6",
  "#EC4899", "#14B8A6", "#F97316", "#EAB308", "#22C55E",
  "#06B6D4", "#A855F7",
];

/** Deterministic palette pick from a production id. */
function colorFor(id) {
  if (!id) return "#71717A"; // masked cross-tenant booking
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

/** HH:MM lex compare → overlap detection for half-open intervals. */
function overlaps(a, b) {
  return a.start_time < b.end_time && b.start_time < a.end_time;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function CrossFacilityCalendar({ embedded = false }) {
  const [cursor, setCursor] = useState(new Date());
  const [facilities, setFacilities] = useState([]);
  const [facilityFilter, setFacilityFilter] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const weekStart = useMemo(() => startOfWeek(cursor), [cursor]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const startISO = ymd(weekDays[0]);
  const endISO = ymd(weekDays[6]);

  useEffect(() => {
    api.get("/facilities").then((r) => setFacilities(r.data || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { start_date: startISO, end_date: endISO };
    if (facilityFilter) params.facility_id = facilityFilter;
    api.get("/facilities/schedule", { params })
      .then((r) => setBookings(r.data || []))
      .finally(() => setLoading(false));
  }, [startISO, endISO, facilityFilter]);

  // Group bookings per day for the grid.
  const byDay = useMemo(() => {
    const g = {};
    for (const d of weekDays) g[ymd(d)] = [];
    for (const b of bookings) {
      if (g[b.date]) g[b.date].push(b);
    }
    // Sort each day by start_time so chips lay out chronologically.
    for (const k of Object.keys(g)) g[k].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    return g;
  }, [bookings, weekDays]);

  // Compute cross-production overlaps within each facility per day. If two
  // rehearsals from DIFFERENT productions in the SAME facility overlap in
  // time, that's the double-book we want to warn about.
  const overlapIds = useMemo(() => {
    const out = new Set();
    for (const day of Object.keys(byDay)) {
      const items = byDay[day];
      for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
          const a = items[i], b = items[j];
          if (!a.facility_id || a.facility_id !== b.facility_id) continue;
          if (a.production_id && b.production_id && a.production_id === b.production_id) continue;
          if (overlaps(a, b)) {
            out.add(a.id); out.add(b.id);
          }
        }
      }
    }
    return out;
  }, [byDay]);

  // Legend: unique shows in view.
  const legend = useMemo(() => {
    const seen = new Map();
    for (const b of bookings) {
      const key = b.production_id || "__masked";
      if (!seen.has(key)) {
        seen.set(key, {
          id: b.production_id,
          title: b.production_id ? (b.production_title || "Untitled show") : "Another production",
          color: colorFor(b.production_id),
        });
      }
    }
    return Array.from(seen.values()).slice(0, 20);
  }, [bookings]);

  const nudge = (days) => setCursor(addDays(cursor, days));
  const today = () => setCursor(new Date());
  const facilityById = useMemo(
    () => Object.fromEntries(facilities.map((f) => [f.id, f])),
    [facilities]
  );

  return (
    <div className={embedded ? "space-y-4" : "space-y-6"} data-testid="cross-facility-calendar">
      {!embedded && (
        <header>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Venue</div>
          <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Cross-facility calendar</h1>
        </header>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-[#121212] p-1">
          <button
            data-testid="xf-prev-week"
            onClick={() => nudge(-7)}
            className="h-8 w-8 grid place-items-center rounded hover:bg-white/5"
            title="Previous week"
          ><ChevronLeft className="w-4 h-4" /></button>
          <button
            data-testid="xf-today"
            onClick={today}
            className="h-8 px-3 rounded text-xs hover:bg-white/5"
          >Today</button>
          <button
            data-testid="xf-next-week"
            onClick={() => nudge(7)}
            className="h-8 w-8 grid place-items-center rounded hover:bg-white/5"
            title="Next week"
          ><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="text-sm text-zinc-400">
          <Layers className="inline w-3.5 h-3.5 mr-1 text-gold" />
          {weekDays[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          {" — "}
          {weekDays[6].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
        </div>
        <label className="text-xs text-zinc-400 inline-flex items-center gap-2">
          <Building2 className="w-3.5 h-3.5" />
          <select
            data-testid="xf-facility-filter"
            value={facilityFilter}
            onChange={(e) => setFacilityFilter(e.target.value)}
            className="bg-[#0A0A0A] border border-white/10 rounded h-8 px-2 text-xs"
          >
            <option value="">Every facility</option>
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Legend */}
      {legend.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap text-[11px]" data-testid="xf-legend">
          <span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-1">Shows</span>
          {legend.map((s) => (
            <span
              key={s.id || "masked"}
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-white/10 bg-[#0A0A0A]"
            >
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
              <span className="text-zinc-300 max-w-[140px] truncate">{s.title}</span>
            </span>
          ))}
        </div>
      )}

      {/* Week grid — 1-col stack on phones so each day is a legible tap
          target, 3-up on tablets, full 7-col row on desktop. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-7 gap-2 min-h-[420px]">
        {weekDays.map((d) => {
          const key = ymd(d);
          const items = byDay[key] || [];
          const isToday = ymd(new Date()) === key;
          return (
            <div
              key={key}
              data-testid={`xf-day-${key}`}
              className={`rounded-lg border ${isToday ? "border-gold/40" : "border-white/10"} bg-[#0A0A0A] flex flex-col`}
            >
              <div className={`px-3 py-2 border-b flex items-center justify-between sm:block ${isToday ? "border-gold/30 bg-gold/5" : "border-white/5"}`}>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {WEEKDAYS[d.getDay()]} <span className="sm:hidden text-zinc-600">· {d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                </div>
                <div className={`hidden sm:block text-sm font-semibold ${isToday ? "text-gold" : "text-zinc-200"}`}>
                  {d.getDate()}
                </div>
                <div className="sm:hidden text-[10px] text-zinc-500">{items.length ? `${items.length} booking${items.length === 1 ? "" : "s"}` : ""}</div>
              </div>
              <div className="flex-1 p-1.5 space-y-1.5 overflow-y-auto max-h-[520px]">
                {items.length === 0 ? (
                  <div className="text-[10px] text-zinc-600 text-center py-6">—</div>
                ) : items.map((b) => {
                  const color = colorFor(b.production_id);
                  const masked = !b.production_id;
                  const isOverlap = overlapIds.has(b.id);
                  const fac = facilityById[b.facility_id];
                  return (
                    <div
                      key={b.id}
                      data-testid={`xf-booking-${b.id}`}
                      className={`rounded-md px-2 py-1.5 text-[11px] leading-tight border-l-2 relative
                        ${masked ? "bg-zinc-800/40 text-zinc-500" : "bg-white/5 text-zinc-100"}
                        ${isOverlap ? "ring-1 ring-amber-400/60" : ""}`}
                      style={{ borderLeftColor: color }}
                      title={masked ? "Booked by another production" : `${b.production_title} — ${b.title || ""}`}
                    >
                      <div className="font-mono text-[10px] text-zinc-400">
                        {fmtTimeRange(b.start_time, b.end_time)}
                      </div>
                      <div className="mt-0.5 truncate font-medium">
                        {b.title || (masked ? "Booked" : "—")}
                      </div>
                      <div className="mt-0.5 text-[10px] text-zinc-500 truncate">
                        {fac?.name ? `${fac.name} · ` : ""}{b.space_name}
                      </div>
                      {!masked && (
                        <div className="mt-0.5 text-[10px] opacity-80 truncate" style={{ color }}>
                          {b.production_title}
                        </div>
                      )}
                      {isOverlap && (
                        <div
                          data-testid={`xf-overlap-${b.id}`}
                          className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-300"
                        >
                          <AlertTriangle className="w-3 h-3" /> Double-book
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {loading && (
        <div className="text-[11px] text-zinc-500 text-center">Loading week…</div>
      )}
      {!loading && bookings.length === 0 && (
        <div className="rounded border border-dashed border-white/10 py-8 text-center text-xs text-zinc-500">
          No bookings this week across any facility.
        </div>
      )}
    </div>
  );
}
